/* eslint-disable react/no-danger */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import moment from 'moment';
import { ReactSearchAutocomplete } from 'react-search-autocomplete';
import { Loader, useLoader } from '@panel/commons/lib/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { USER_TYPE } from '@panel/commons/constants';
import { select } from '@panel/commons/core/themev2';
import { useModal } from '@panel/commons/hooks';
import { Modal, ModalBase as ModalContainer } from '@panel/commons';
import {
  getIntegrationsState,
  getSubCompanies,
  listIntegrators,
  getCurrentIntegration,
  clearUpsertIntegration
} from '../ducks';
import { clearCurrentIntegration, removeXMLIntegration, upsertIntegration } from '../ducks/actions';
import {
  BannerContainer,
  ErrorMessage,
  FormControl,
  InputWrapper,
  LoaderWrapper,
  SearchResult,
  StyledConfirm,
  StyledConfirmBody,
  StyledConfirmButton,
  StyledInputWrapper,
  StyledLabel,
  StyledWrapper,
  Submit,
  TextInput,
  Title,
  ToggleContainer,
  ToggleInput,
  ToggleLabel,
  ToggleTitle
} from './styles';
import { INTEGRATIONS } from '../constants';
import { PostingsIntegrationsVisualizer } from './postingIntegrationsVisualizer';

const DEFAULT_BANNER_IDLE = {
  title: '',
  message: '',
  type: 'idle'
};

// Schema validacion
const formSchema = yup.object().shape({
  companyId: yup.number().when('$companies', (companies, schema) => {
    if (companies?.length === 0) {
      return schema.nullable().optional();
    }
    return schema.required();
  }),
  integratorId: yup.number().required(),
  companyCode: yup
    .string()
    .when(['$integrators', 'integratorId', 'integrationType'], (integrators, integratorId, integrationType, schema) => {
      const obj = integrators.find(it => it.id === integratorId);
      if (integrationType === INTEGRATIONS.XML && obj?.code === true) {
        return schema.required();
      }
      return schema.nullable().optional();
    }),
  fileURL: yup
    .string()
    .url()
    .when('integrationType', (integrationType, schema) => {
      if (integrationType === INTEGRATIONS.XML) {
        return schema.required();
      }
      return schema.nullable().optional();
    })
});

const defaultFuzzySearchOptions = {
  shouldSort: true,
  threshold: 0.6,
  location: 0,
  distance: 800,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: ['name']
};

function formatResult(item) {
  return <SearchResult>{item.name}</SearchResult>;
}

function IntegrationPanel({ user }) {
  const { toggleLoader, setToggleLoader } = useLoader();
  const dispatch = useDispatch();
  const lastIntegrator = useRef(undefined);
  const lastCompany = useRef(undefined);
  const [enabledCompanyCode, setEnableCompanyCode] = useState(false);
  const { companies, integrators, loading, activation, companyIntegrations, deactivation } = useSelector(
    getIntegrationsState
  );
  const { register, handleSubmit, control, trigger, setValue, errors, getValues } = useForm({
    defaultValues: { integrationType: INTEGRATIONS.XML },
    context: { companies: companies.data, integrators: integrators.data },
    resolver: yupResolver(formSchema),
    mode: 'onBlur',
    shouldFocusError: true
  });
  const $observer = useWatch({ name: ['companyId', 'integratorId', 'integrationType'], control });
  const [banner, setBanner] = useState(DEFAULT_BANNER_IDLE);
  const { t } = useTranslation();
  const { isShowing: toggleModal, toggleValue: setToggleModal } = useModal();

  const enableCompanySelector = useMemo(() => {
    if (typeof user?.userTypeId === 'undefined') {
      return true;
    }
    return user?.userTypeId === USER_TYPE.ADMINISTRATOR && (companies.data.length > 1 || !companies.fetched);
  }, [user, companies.data.length, companies.fetched]);

  useEffect(() => {
    register('companyId');
    register('integratorId');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      user?.userTypeId === USER_TYPE.ADMINISTRATOR &&
      companies.data.length === 0 &&
      companies.fetched === false &&
      companies.loading === false
    ) {
      dispatch(getSubCompanies());
    }
  }, [user, companies.fetched, companies.loading, companies.data.length, dispatch]);

  useEffect(() => {
    setToggleLoader(!!user?.isLoading);
    if (
      USER_TYPE.SUPERUSER === user?.userTypeId &&
      companyIntegrations.data === null &&
      companyIntegrations.loading === false
    ) {
      dispatch(getCurrentIntegration(user.publisherId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    setToggleLoader(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (integrators.type !== $observer.integrationType && integrators.loading === false) {
      dispatch(listIntegrators($observer.integrationType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrators, $observer.integrationType]);

  useEffect(() => {
    if (activation?.data != null) {
      const now = moment();
      const processTime = now.add(15, 'minutes').format('HH:mm');
      const { companyCode } = activation.data;
      let message = `Sua integração está ativada com o código ${companyCode}. O processamento está programado para começar as ${processTime}!. Dúvidas, entre em contato com SAC: <strong>atendimento@imovelweb.com.br</strong>.`;
      if (activation.data?.integrationType === INTEGRATIONS.API || $observer.integrationType === INTEGRATIONS.API) {
        message = `Sua integração foi ativada com o código ${companyCode}. Agora, configure o código no integrador e selecione os imóveis para divulgação.`;
      }
      setBanner({
        title: 'Sucesso',
        message,
        type: 'success'
      });
      dispatch(getCurrentIntegration(activation.data?.companyId));
    } else if (activation?.error != null) {
      const { data } = activation.error;
      let defaultMessage =
        'Houve um erro ao processar sua solicitação. Verifique os detalhes. Se o erro persistir, entre em contato com o suporte ao cliente pelo e-mail <strong>atendimento@imovelweb.com.br.</strong>';
      if (data?.message && data?.message !== '...') {
        defaultMessage = t(data.message, { integrationType: 'Anuncios' });
      }
      setBanner({
        title: 'Error',
        message: defaultMessage,
        type: 'error'
      });
    } else {
      setBanner(DEFAULT_BANNER_IDLE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activation]);

  useEffect(() => {
    if (deactivation.loading === false && (deactivation.data != null || deactivation.error != null)) {
      setToggleModal(false);
    }
    if (deactivation.data != null) {
      const { integratorName = '' } = deactivation.data;
      setBanner({
        title: 'Sucesso',
        message: t('panel.integrations.commons.errors.ic.deactivate.success', {
          integrationType: 'Anuncios',
          integratorName
        }),
        type: 'success'
      });
      if (companyIntegrations.loading === false) {
        dispatch(getCurrentIntegration(deactivation.data.companyId));
      }
    } else if (deactivation.error != null) {
      const { data } = deactivation.error;
      let integratorName = '--';
      if (companyIntegrations.data !== null && companyIntegrations.data.length > 0) {
        integratorName = companyIntegrations.data[0].integratorName;
      }
      let defaultMessage = t('panel.integrations.commons.errors.ic.deactivate.errors.server', {
        integrationType: 'Anuncios'
      });
      if (data?.message && data?.message !== '...') {
        defaultMessage = t(data.message, {
          integratorName,
          integrationType: 'Anuncios'
        });
      }
      setBanner({
        title: 'Error',
        message: defaultMessage,
        type: 'error'
      });
    } else {
      setBanner(DEFAULT_BANNER_IDLE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deactivation]);

  const selectedCompanyName = useMemo(() => {
    if (user?.userTypeId === USER_TYPE.SUPERUSER) {
      return user?.realEstateName;
    }
    if (user?.userTypeId === USER_TYPE.ADMINISTRATOR && companies.data.length === 1) {
      return companies.data[0].name;
    }
    return companies.data.find(it => it.id === $observer.companyId)?.name || '';
  }, [user, companies.data, $observer.companyId]);

  const selectedCompanyId = useMemo(() => {
    if (user?.userTypeId === USER_TYPE.SUPERUSER) {
      return user?.publisherId;
    }
    if (user?.userTypeId === USER_TYPE.ADMINISTRATOR && companies.data.length === 1) {
      return companies.data[0].id;
    }
    return $observer.companyId;
  }, [user, companies.data, $observer.companyId]);

  useEffect(() => {
    if (user?.userTypeId === USER_TYPE.ADMINISTRATOR && selectedCompanyId && companyIntegrations.fetched === false) {
      dispatch(getCurrentIntegration(selectedCompanyId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const onFocusCompany = () => {
    // Es Administrador
    if (companies.data.length === 0 && user?.userTypeId === USER_TYPE.ADMINISTRATOR && companies.loading === false) {
      dispatch(getSubCompanies());
    }
  };

  const handleSearchCompany = searchStr => {
    if (companies.data.length === 0 && user?.userTypeId === USER_TYPE.ADMINISTRATOR && companies.loading === false) {
      dispatch(getSubCompanies());
    }
    if (!!lastCompany.current && lastCompany.current !== searchStr) {
      setValue('companyId', undefined);
      dispatch(clearCurrentIntegration());
    }
  };

  const handleSelectCompany = ({ id, name }) => {
    setValue('companyId', id);
    dispatch(getCurrentIntegration(id));
    if (activation.data || activation.error) {
      dispatch(clearUpsertIntegration());
    }
    lastCompany.current = name;
  };

  const onClearCompany = () => {
    setValue('companyId', undefined);
    if (activation.data || activation.error) {
      dispatch(clearUpsertIntegration());
    }
  };

  const onFocusIntegrators = () => {
    if (!!$observer.integrationType && integrators.data.length === 0 && integrators.loading === false) {
      dispatch(listIntegrators($observer.integrationType));
    }
  };

  const handleSearchIntegrator = searchStr => {
    if (!!$observer.integrationType && integrators.data.length === 0 && integrators.loading === false) {
      dispatch(listIntegrators($observer.integrationType));
    }
    if (!!lastIntegrator.current && lastIntegrator.current !== searchStr) {
      setValue('integratorId', undefined);
    }
  };

  const handleSelectIntegrator = ({ code = false, name, id }) => {
    setEnableCompanyCode(code);
    setValue('integratorId', id);
    lastIntegrator.current = name;
  };

  const onClearIntegrator = () => {
    setValue('integratorId', '');
  };

  const handleDeactivateIntegrationConfirm = () => {
    if (!companyIntegrations.data || companyIntegrations.data.length === 0) {
      return;
    }
    const { companyId, interfaceId } = companyIntegrations.data[0];
    if (typeof companyId !== 'undefined' && typeof interfaceId !== 'undefined' && deactivation.loading === false) {
      dispatch(removeXMLIntegration(companyId, interfaceId));
    }
  };

  const onSubmit = data => {
    dispatch(upsertIntegration(data));
  };

  const onError = err => {
    if (process.env?.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(err);
      // eslint-disable-next-line no-console
      console.info(getValues());
    }
  };

  return (
    <div className='container'>
      {toggleLoader && (
        <LoaderWrapper>
          <Loader isUrbania={process.env.REACT_APP_BUILD_SITE === 'UBPE'} />
        </LoaderWrapper>
      )}
      <Title>{t('panel.integrations.commons.form.title')}</Title>
      {banner?.type !== 'idle' && (
        <BannerContainer type={banner?.type}>
          <h3>{banner?.title}</h3>
          <div dangerouslySetInnerHTML={{ __html: banner?.message }} />
        </BannerContainer>
      )}
      <form onSubmit={handleSubmit(onSubmit, onError)} autoComplete='false' noValidate>
        <ToggleTitle>{t('panel.integrations.commons.form.type')}</ToggleTitle>
        <ToggleContainer>
          <ToggleInput
            defaultChecked
            disabled={integrators.loading}
            type='radio'
            id='integrationXML'
            name='integrationType'
            value={INTEGRATIONS.XML}
            ref={register}
          />
          <ToggleLabel htmlFor='integrationXML'>XML</ToggleLabel>
          <ToggleInput
            disabled={integrators.loading}
            type='radio'
            id='integrationAPI'
            name='integrationType'
            value={INTEGRATIONS.API}
            ref={register}
          />
          <ToggleLabel htmlFor='integrationAPI'>API</ToggleLabel>
        </ToggleContainer>
        {enableCompanySelector && (
          <FormControl className='column'>
            <StyledWrapper>
              <StyledInputWrapper className='search'>
                <ReactSearchAutocomplete
                  name='companies'
                  items={companies.data}
                  showIcon={false}
                  showClear={false}
                  inputDebounce={200}
                  onFocus={onFocusCompany}
                  onClear={onClearCompany}
                  onSelect={handleSelectCompany}
                  onSearch={handleSearchCompany}
                  onBlur={() => trigger('companyId')}
                  styling={{
                    marginTop: '18px',
                    position: 'relative',
                    zIndex: 9,
                    fontSize: '14px',
                    lineHeight: '22px',
                    border: `1px solid ${select('midDarkGray')}`,
                    height: '48px',
                    boxShadow: '0px',
                    borderRadius: '8px',
                    fontFamily: 'Hind'
                  }}
                  className='search-input'
                  autoFocus={false}
                  maxLength={100}
                  maxResults={8}
                  fuseOptions={defaultFuzzySearchOptions}
                  showNoResultsText='Sem resultados de pesquisa'
                  formatResult={formatResult}
                />
                <StyledLabel htmlFor='companies' hasContent={!!$observer.companyId} style={{ zIndex: '10' }}>
                  Sucursal
                </StyledLabel>
              </StyledInputWrapper>
              {errors.companyId && (
                <ErrorMessage>{t('panel.integrations.commons.validations.errors.required')}</ErrorMessage>
              )}
            </StyledWrapper>
          </FormControl>
        )}
        {!enableCompanySelector && (
          <FormControl>
            <StyledInputWrapper className='single-company'>
              {USER_TYPE.ADMINISTRATOR === user?.userTypeId && (
                <input type='hidden' name='companyId' value={selectedCompanyId} ref={register} />
              )}
              <TextInput type='text' defaultValue={selectedCompanyName} disabled />
              <StyledLabel htmlFor='companyName'>Sucursal</StyledLabel>
            </StyledInputWrapper>
          </FormControl>
        )}

        {companyIntegrations.data?.length > 0 &&
          companyIntegrations.data.map(ci => (
            <FormControl className='info' key={`${ci.integratorId}-${ci.interfaceId}-${ci.companyCode}`}>
              <PostingsIntegrationsVisualizer user={user} integration={ci} onRemove={() => setToggleModal(true)} />
            </FormControl>
          ))}
        <FormControl>
          <StyledWrapper>
            <StyledInputWrapper className='search'>
              <ReactSearchAutocomplete
                name='search'
                items={integrators.data}
                showIcon={false}
                showClear={false}
                inputDebounce={200}
                onClear={onClearIntegrator}
                onSearch={handleSearchIntegrator}
                onFocus={onFocusIntegrators}
                onSelect={handleSelectIntegrator}
                onBlur={() => trigger('integratorId')}
                styling={{
                  marginTop: '18px',
                  position: 'relative',
                  zIndex: 7,
                  fontSize: '14px',
                  lineHeight: '22px',
                  border: `1px solid ${select('midDarkGray')}`,
                  height: '48px',
                  boxShadow: '0px',
                  borderRadius: '8px',
                  fontFamily: 'Hind'
                }}
                maxLength={100}
                maxResults={8}
                fuseOptions={defaultFuzzySearchOptions}
                className='search-input'
                autoFocus
                showNoResultsText='Sem resultados de pesquisa'
                formatResult={formatResult}
              />
              <StyledLabel htmlFor='search' hasContent={!!$observer.integratorId} style={{ zIndex: '8' }}>
                Integrador
              </StyledLabel>
            </StyledInputWrapper>
            {errors.integratorId && (
              <ErrorMessage>{t('panel.integrations.commons.validations.errors.required')}</ErrorMessage>
            )}
          </StyledWrapper>

          {$observer.integrationType === INTEGRATIONS.XML && enabledCompanyCode && (
            <StyledWrapper>
              <StyledInputWrapper>
                <TextInput type='text' defaultValue='' name='companyCode' required ref={register} />
                <StyledLabel htmlFor='companyCode'>{t('panel.integrations.commons.fields.companycode')}</StyledLabel>
              </StyledInputWrapper>
              {errors.companyCode && (
                <ErrorMessage>{t('panel.integrations.commons.validations.errors.required')}</ErrorMessage>
              )}
            </StyledWrapper>
          )}
        </FormControl>
        {$observer.integrationType === INTEGRATIONS.XML && (
          <FormControl>
            <StyledWrapper>
              <StyledInputWrapper>
                <TextInput type='text' defaultValue='' name='fileURL' ref={register()} />
                <StyledLabel htmlFor='fileURL'>XML</StyledLabel>
              </StyledInputWrapper>
              {errors.fileURL && (
                <ErrorMessage>
                  {errors.fileURL?.type === 'url'
                    ? t('panel.integrations.xml.errors.url.invalid')
                    : t('panel.integrations.commons.validations.errors.required')}
                </ErrorMessage>
              )}
            </StyledWrapper>
          </FormControl>
        )}
        <FormControl>
          <InputWrapper>
            <Submit type='submit' disabled={loading}>
              Salvar
            </Submit>
          </InputWrapper>
        </FormControl>
      </form>
      {toggleModal && (
        <Modal>
          <ModalContainer
            isLoading={deactivation.loading}
            height='190px'
            openModal={toggleModal}
            onClose={() => setToggleModal(false)}
            withoutOutsideClose
            withoutCloseButton
          >
            <StyledConfirm>
              <StyledConfirmBody>
                <div className='confirm-text'>
                  {t('panel.integrations.commons.deactivate.confirm.message', { integrationType: 'Anuncios' })}
                </div>
                <div className='btn-container'>
                  <StyledConfirmButton onClick={() => setToggleModal(false)} cancel>
                    {t('panel.integrations.commons.deactivate.confirm.btn.cancel')}
                  </StyledConfirmButton>
                  <StyledConfirmButton onClick={handleDeactivateIntegrationConfirm} ok>
                    {t('panel.integrations.commons.deactivate.confirm.btn.ok')}
                  </StyledConfirmButton>
                </div>
              </StyledConfirmBody>
            </StyledConfirm>
          </ModalContainer>
        </Modal>
      )}
    </div>
  );
}

export { IntegrationPanel };
